import type { Form } from '../../../services/formService';

type FormField = Form['fields'][number];

/** Matches EffortSummary / RB summary table row shape */
export interface MechanicEffortSummaryRow {
  type: string;
  description: string;
  quantity: number;
  rate: number;
  totalCost: number;
  unitOfMeasure: string;
  task: string;
  taskName?: string;
  asset?: string;
  assetNumber?: string;
  homeForemanAreaId?: number;
  taskId: number;
  comments?: string;
  /** Odometer / Mileage value from the WorkCompleted unit (Vehicle Work and Repairs activities). */
  mileage?: string;
  excludeFromQuantityTotal?: boolean;
  /** WorkCompleted group id for DRM OT lineKey (server `Mechanic|group=…`). */
  mechanicGroupId?: string;
  otBreakdown?: string;
}

/** Stable DRM OT allocation line key (must match server `buildMechanicSubmissionLabourLineKey`). */
export function buildMechanicSubmissionLabourLineKey(groupId: string): string {
  return `Mechanic|group=${groupId}`;
}

/** Fallback line key when only TotalLabourTimeDec is present. */
export const MECHANIC_SUBMISSION_LABOUR_LINE_KEY_FALLBACK = 'Mechanic|group=form';

export interface MechanicDefect {
  defectId: string;
  category: string;
  comment: string;
  completed: string;
}

export interface MechanicWorkCompletedRow {
  groupId: string;
  /** Vehicle / equipment line from the work block (e.g. "Unit #123"). */
  unitLabel: string;
  activityType: string;
  activity: string;
  hoursWorked: string;
  defects: MechanicDefect[];
}

/** Values on DefectCompleted that mean not done (case-insensitive). */
const DEFECT_COMPLETED_NEGATIVE = new Set([
  '',
  'no',
  'n',
  'false',
  '0',
  'pending',
  'open',
  'not completed',
  'not fixed',
  'incomplete',
  'not started'
]);

/**
 * Whether the mechanic defect row is marked fixed/completed in the form.
 * Any non-empty value not in {@link DEFECT_COMPLETED_NEGATIVE} counts as completed (e.g. Yes, true, Done).
 */
export function isMechanicDefectMarkedCompleted(completedValue: string | undefined): boolean {
  const v = (completedValue || '').trim().toLowerCase();
  if (!v) return false;
  return !DEFECT_COMPLETED_NEGATIVE.has(v);
}

export const isMechanicDailyLog = (form: Form): boolean => {
  const formName = (form.name || '').toLowerCase();
  if (formName.includes('mechanic')) {
    return true;
  }

  return form.fields.some((field) =>
    field.field === 'ActivityType' ||
    field.field === 'DefectID' ||
    field.path.includes('MechanicTasks')
  );
};

const safeParseFloat = (value: string | undefined, defaultValue: number = 0): number => {
  if (!value) return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

/** Parse TotalTimeSpent / duration JSON or HH:MM:SS into decimal hours */
export function parseHoursFromStoredDuration(value: unknown): number {
  if (value == null || !String(value).trim()) return 0;
  if (Array.isArray(value)) {
    for (const entry of value) {
      const parsed = parseHoursFromStoredDuration(entry);
      if (parsed > 0) return parsed;
    }
    return 0;
  }
  if (typeof value === 'object') {
    const parsedObj = value as { millis?: unknown; display?: unknown };
    if (typeof parsedObj.millis === 'number' && Number.isFinite(parsedObj.millis)) {
      return parsedObj.millis / 3600000;
    }
    if (typeof parsedObj.display === 'string') {
      return parseHoursFromHms(parsedObj.display);
    }
  }
  const trimmed = String(value).trim();
  try {
    const parsed = JSON.parse(trimmed) as
      | { millis?: number; display?: string }
      | Array<{ millis?: number; display?: string }>;
    if (Array.isArray(parsed)) {
      for (const entry of parsed) {
        const entryHours = parseHoursFromStoredDuration(entry);
        if (entryHours > 0) return entryHours;
      }
      return 0;
    }
    if (parsed && typeof parsed.millis === 'number') {
      return parsed.millis / 3600000;
    }
    if (parsed?.display && typeof parsed.display === 'string') {
      return parseHoursFromHms(parsed.display);
    }
  } catch {
    /* not JSON */
  }
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    return parseHoursFromHms(trimmed);
  }
  return safeParseFloat(trimmed);
}

/** Human-readable label for TrueContext Duration (`{ millis, display }`, JSON string, or HMS). */
export function formatStoredDurationAnswerForDisplay(value: unknown): string {
  if (value == null) return '—';
  if (Array.isArray(value)) {
    for (const entry of value) {
      const label = formatStoredDurationAnswerForDisplay(entry);
      if (label !== '—') return label;
    }
    return '—';
  }
  if (typeof value === 'object') {
    const parsedObj = value as { millis?: unknown; display?: unknown };
    if (typeof parsedObj.display === 'string' && parsedObj.display.trim() !== '') {
      return parsedObj.display.trim();
    }
    if (typeof parsedObj.millis === 'number' && Number.isFinite(parsedObj.millis)) {
      const abs = Math.abs(Math.round(parsedObj.millis));
      const sign = parsedObj.millis < 0 ? '-' : '';
      const h = Math.floor(abs / 3600000);
      const m = Math.floor((abs % 3600000) / 60000);
      const s = Math.floor((abs % 60000) / 1000);
      return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return '—';
  }
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === '[object Object]') return '—';
  try {
    const parsed = JSON.parse(trimmed) as
      | { millis?: number; display?: string }
      | Array<{ millis?: number; display?: string }>;
    if (Array.isArray(parsed)) {
      for (const entry of parsed) {
        const label = formatStoredDurationAnswerForDisplay(entry);
        if (label !== '—') return label;
      }
      return '—';
    }
    if (parsed && typeof parsed.display === 'string' && parsed.display.trim() !== '') {
      return parsed.display.trim();
    }
    if (parsed && typeof parsed.millis === 'number' && Number.isFinite(parsed.millis)) {
      return formatStoredDurationAnswerForDisplay(parsed);
    }
  } catch {
    /* not JSON */
  }
  if (/^\d{1,3}:\d{2}(:\d{2})?$/.test(trimmed)) {
    return trimmed;
  }
  return trimmed;
}

function parseHoursFromHms(s: string): number {
  const parts = s.split(':').map((p) => parseFloat(p));
  if (parts.some((n) => isNaN(n))) return 0;
  const h = parts[0] || 0;
  const m = parts[1] || 0;
  const sec = parts[2] || 0;
  return h + m / 60 + sec / 3600;
}

/**
 * Canonical work-completed bucket id: "1.{index}" so normalized (Tasks 1.N) and
 * legacy TrueContext paths (New Page.WorkCompleted.N / WorkCompleted.N.WorkCompleted) merge.
 */
export function extractWorkCompletedGroupId(path: string): string | null {
  if (!path.includes('WorkCompleted')) return null;

  const normalized = path.match(/WorkCompleted\.Tasks (\d+\.\d+)/);
  if (normalized) return normalized[1];

  const newPage = path.match(/New Page\.WorkCompleted\.(\d+)(?:\.|$)/);
  if (newPage) return `1.${newPage[1]}`;

  const legacy = path.match(/WorkCompleted\.(\d+)\.WorkCompleted(?:\.|$)/);
  if (legacy) return `1.${legacy[1]}`;

  return null;
}

function parseRate(value: string | undefined): number {
  if (!value) return 0;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return isNaN(n) ? 0 : n;
}

/** Sort canonical work keys `1.0`, `1.1`, … for UI tabs. */
export function sortMechanicGroupKeys(a: string, b: string): number {
  const score = (id: string): number[] => {
    const [baseId, mechanicTaskIndex] = id.split('::');
    const baseParts = (baseId || '')
      .split('.')
      .map((part) => Number(part))
      .filter((part) => !Number.isNaN(part));
    const major = baseParts[0] ?? 0;
    const minor = baseParts[1] ?? 0;
    const activity = mechanicTaskIndex != null && mechanicTaskIndex !== ''
      ? Number(mechanicTaskIndex)
      : -1;
    return [major, minor, Number.isNaN(activity) ? -1 : activity];
  };
  const [aMajor, aMinor, aActivity] = score(a);
  const [bMajor, bMinor, bActivity] = score(b);
  if (aMajor !== bMajor) return aMajor - bMajor;
  if (aMinor !== bMinor) return aMinor - bMinor;
  return aActivity - bActivity;
}

function sortGroupKeys(a: string, b: string): number {
  return sortMechanicGroupKeys(a, b);
}

function resolveMechanicActivityNumberForTitle(groupFields: FormField[]): string {
  const fromActivityNumber = groupFields.find(
    (f) =>
      f.field === 'ActivityNumber' &&
      f.path.includes('MechanicTasks') &&
      (f.path.includes('.Tasks.Activity.') || f.path.includes('Tasks.Activity'))
  )?.value?.trim();
  if (fromActivityNumber) return fromActivityNumber;
  const fromTimber = groupFields.find(
    (f) =>
      f.field === 'ActivityNumberTimber' &&
      f.path.includes('MechanicTasks') &&
      f.path.includes('ActivityMetadata')
  )?.value?.trim();
  if (fromTimber) return fromTimber;
  return (
    groupFields.find(
      (f) =>
        f.field === 'Activity' &&
        f.path.includes('MechanicTasks') &&
        (f.path.includes('.Tasks.Activity.') || f.path.includes('Tasks.Activity'))
    )?.value?.trim() || ''
  );
}

/** Tab title: Activity name + activity number when both exist (Detailed View). */
export function getMechanicActivityTabTitle(groupFields: Form['fields'], groupId: string): string {
  const name =
    groupFields.find(
      (f) =>
        f.field === 'ActivityName' &&
        (f.path.includes('ActivityMetadata') || f.path.includes('MechanicTasks'))
    )?.value?.trim() || '';
  const num = resolveMechanicActivityNumberForTitle(groupFields);
  const unitLabel = pickMechanicUnitDescriptionFromGroup(groupFields);
  const withUnit = (base: string): string => {
    if (!unitLabel) return base;
    return `${base} - ${unitLabel}`;
  };
  if (name && num) {
    if (name.includes(num)) return name;
    return withUnit(`${name} (${num})`);
  }
  if (name) return withUnit(name);
  if (num) return withUnit(`Activity ${num}`);
  return withUnit(`Work ${groupId}`);
}

/** Human-readable duration for TrueContext DurationPicker / calculation JSON. */
export function formatMechanicDurationDisplay(value: string | undefined): string {
  if (!value || !String(value).trim()) return '';
  const trimmed = String(value).trim();
  try {
    const parsed = JSON.parse(trimmed) as { millis?: number; display?: string };
    if (parsed?.display && String(parsed.display).trim()) return String(parsed.display).trim();
    if (typeof parsed?.millis === 'number' && parsed.millis > 0) {
      const h = parsed.millis / 3600000;
      const m = (parsed.millis % 3600000) / 60000;
      if (h >= 1) return `${h.toFixed(2)} h`;
      return `${Math.round(m)} min`;
    }
  } catch {
    /* not JSON */
  }
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) return trimmed;
  return trimmed;
}

/** VehicleUsage subsection fields for Detailed View (only non-empty pieces returned). */
export function getMechanicVehicleUsageFieldsForDisplay(g: Form['fields']): {
  vehicleUsed: string;
  durationLabel: string;
  vehicleRate: string;
} | null {
  const vehicleUsed =
    g.find((f) => f.field === 'VehicleUsed' && f.path.includes('VehicleUsage'))?.value?.trim() || '';
  const durationRaw = g.find(
    (f) => f.field === 'DurationUsage' && f.path.includes('VehicleUsage')
  )?.value;
  const vehicleRate =
    g.find((f) => f.field === 'VehicleRate' && f.path.includes('VehicleUsage'))?.value?.trim() || '';
  const durationLabel =
    formatMechanicDurationDisplay(durationRaw) || String(durationRaw || '').trim();
  if (!vehicleUsed && !durationLabel && !vehicleRate) return null;
  return { vehicleUsed, durationLabel, vehicleRate };
}

function pickMechanicActivityCommentsFromGroup(g: FormField[]): string {
  return (
    g.find(
      (f) =>
        f.field === 'Comments' &&
        f.path.includes('MechanicTasks') &&
        (f.path.includes('.Tasks.Activity.') || f.path.includes('Tasks.Activity'))
    )?.value?.trim() || ''
  );
}

/** Decimal hours string for Detailed View, or empty if none. */
export function formatMechanicHoursWorkedFromGroupFields(g: Form['fields']): string {
  const taskDur =
    g.find(
      (f) =>
        f.field === 'TaskDurationHours' &&
        (f.path.includes('Timecard and Job C') || f.path.includes('Timecard'))
    )?.value ?? g.find((f) => f.field === 'TaskDurationHours')?.value;
  const totalTimeVal = g.find(
    (f) =>
      f.field === 'TotalTime' &&
      (f.path.includes('MechanicTasks') ||
        f.path.includes('.Tasks.Activity') ||
        f.path.includes('.Activity.'))
  )?.value;
  const totalTimeSpentVal = g.find(
    (f) =>
      f.field === 'TotalTimeSpent' &&
      (f.path.includes('MechanicTasks') ||
        f.path.includes('Activity') ||
        f.path.includes('.Tasks.Activity'))
  )?.value;
  const rawTimeValue = g.find(
    (f) =>
      f.field === 'RawTimeValue' &&
      (f.path.includes('MechanicTasks') ||
        f.path.includes('Activity') ||
        f.path.includes('.Tasks.Activity'))
  )?.value;

  // Match summary behavior: prefer activity-level duration over shared task-level duration.
  let h = parseFloat(String(totalTimeVal || '0'));
  if (isNaN(h) || h <= 0) h = parseHoursFromStoredDuration(totalTimeSpentVal);
  if (isNaN(h) || h <= 0) h = parseHoursFromStoredDuration(rawTimeValue);
  if (isNaN(h) || h <= 0) h = parseFloat(String(taskDur || '0'));
  if (h > 0) return h.toFixed(2);
  return '';
}

export function resolveMechanicLabourHours(
  groupFields: FormField[],
  options?: { preferActivityDuration?: boolean }
): number {
  const taskDur =
    groupFields.find(
      (field) =>
        field.field === 'TaskDurationHours' &&
        (field.path.includes('Timecard and Job C') || field.path.includes('Timecard'))
    )?.value ?? groupFields.find((field) => field.field === 'TaskDurationHours')?.value;
  const totalTimeVal = groupFields.find(
    (field) =>
      field.field === 'TotalTime' &&
      (field.path.includes('MechanicTasks') ||
        field.path.includes('.Tasks.Activity') ||
        field.path.includes('.Activity.'))
  )?.value;
  const totalTimeSpentVal = groupFields.find(
    (field) =>
      field.field === 'TotalTimeSpent' &&
      (field.path.includes('MechanicTasks') ||
        field.path.includes('Activity') ||
        field.path.includes('.Tasks.Activity'))
  )?.value;
  const rawTimeValue = groupFields.find(
    (field) =>
      field.field === 'RawTimeValue' &&
      (field.path.includes('MechanicTasks') ||
        field.path.includes('Activity') ||
        field.path.includes('.Tasks.Activity'))
  )?.value;

  const parseActivityDurationHours = (): number => {
    const totalTimeHours = safeParseFloat(totalTimeVal);
    if (totalTimeHours > 0) return totalTimeHours;
    const totalTimeSpentHours = parseHoursFromStoredDuration(totalTimeSpentVal);
    if (totalTimeSpentHours > 0) return totalTimeSpentHours;
    const rawHours = parseHoursFromStoredDuration(rawTimeValue);
    if (rawHours > 0) return rawHours;
    return 0;
  };

  if (options?.preferActivityDuration) {
    const activityHours = parseActivityDurationHours();
    // Split activity rows must not fall back to shared WorkCompleted TaskDurationHours,
    // otherwise the first activity's duration can be copied to subsequent activities.
    return activityHours;
  }

  let hours = safeParseFloat(taskDur);
  if (hours <= 0) hours = safeParseFloat(totalTimeVal);
  if (hours <= 0) hours = parseHoursFromStoredDuration(totalTimeSpentVal);
  if (hours <= 0) hours = parseHoursFromStoredDuration(rawTimeValue);
  return hours;
}

export function groupMechanicFieldsByWorkIndex(form: Form): Record<string, Form['fields']> {
  const baseGroups: Record<string, Form['fields']> = {};
  for (const field of form.fields) {
    const groupId = extractWorkCompletedGroupId(field.path);
    if (!groupId) continue;
    if (!baseGroups[groupId]) baseGroups[groupId] = [];
    baseGroups[groupId].push(field);
  }

  const expandedGroups: Record<string, Form['fields']> = {};
  Object.entries(baseGroups).forEach(([groupId, groupFields]) => {
    const activityInstanceKeys = Array.from(
      new Set(
        groupFields.flatMap((field) => {
          const match = field.path.match(/MechanicTasks\.(\d+)\.Tasks(?:\.(\d+))?(?:\.|$)/);
          if (!match) return [];
          const mechanicTaskIndex = match[1];
          const taskInstanceIndex = match[2] ?? '0';
          return [`${mechanicTaskIndex}.${taskInstanceIndex}`];
        })
      )
    ).sort((a, b) => {
      const [aMechanic, aTask] = a.split('.').map((v) => Number(v));
      const [bMechanic, bTask] = b.split('.').map((v) => Number(v));
      if (aMechanic !== bMechanic) return aMechanic - bMechanic;
      return aTask - bTask;
    });

    if (activityInstanceKeys.length <= 1) {
      expandedGroups[groupId] = groupFields;
      return;
    }

    activityInstanceKeys.forEach((instanceKey) => {
      const [mechanicTaskIndex, taskInstanceIndex] = instanceKey.split('.');
      const activityGroupId = `${groupId}::${mechanicTaskIndex}.${taskInstanceIndex}`;
      expandedGroups[activityGroupId] = groupFields.filter((field) => {
        if (!field.path.includes('MechanicTasks.')) return true;
        const instanceMatch = field.path.match(/MechanicTasks\.(\d+)\.Tasks(?:\.(\d+))?(?:\.|$)/);
        if (!instanceMatch) return false;
        const fieldMechanicTaskIndex = instanceMatch[1];
        const fieldTaskInstanceIndex = instanceMatch[2] ?? '0';
        return (
          fieldMechanicTaskIndex === mechanicTaskIndex &&
          fieldTaskInstanceIndex === taskInstanceIndex
        );
      });
    });
  });

  return expandedGroups;
}

/** Matches TrueContext "Vehicle Work and Repairs" (spacing/case tolerant). */
function isVehicleWorkAndRepairsActivityType(activityType: string | undefined): boolean {
  const t = (activityType || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t) return false;
  return t === 'vehicle work and repairs' || t.includes('vehicle work and repairs');
}

/**
 * Vehicle / equipment unit for display (TrueContext: Unit dropdown under WorkCompleted…Unit, or legacy fields).
 * After API normalization, paths look like `WorkCompleted.Tasks 1.N.Unit.Unit` (not `.WorkCompleted.Unit`).
 */
function pickMechanicUnitDescriptionFromGroup(g: FormField[]): string {
  const raw =
    g.find((f) => f.field === 'OtherVehicleDescri 1')?.value ||
    g.find((f) => f.field === 'VehicleUnitNumber 1')?.value ||
    g.find(
      (f) =>
        f.field === 'Unit' &&
        f.path.includes('WorkCompleted') &&
        !f.path.includes('DefectVehicle')
    )?.value ||
    g.find((f) => f.field === 'MechanicTimberline')?.value ||
    '';
  const t = (raw || '').trim();
  if (!t) return '';
  if (/^unit\s*#/i.test(t)) return t;
  return `Unit #${t}`;
}

/**
 * Vehicle / equipment unit label for a WorkCompleted group (same source as labour rows in the summary table).
 */
export function getMechanicVehicleUnitForWorkCompletedGroup(form: Form, groupId: string): string {
  const groups = groupMechanicFieldsByWorkIndex(form);
  return pickMechanicUnitDescriptionFromGroup(groups[groupId] || []);
}

/**
 * Odometer / Mileage value for a WorkCompleted group (TrueContext `Mileage` under WorkCompleted…Unit).
 * Returned as the raw string so units / formatting from the form are preserved.
 */
function pickMechanicMileageFromGroup(g: FormField[]): string {
  const raw =
    g.find(
      (f) =>
        f.field === 'Mileage' &&
        f.path.includes('WorkCompleted') &&
        !f.path.includes('Defect')
    )?.value ||
    g.find((f) => f.field === 'Mileage')?.value ||
    '';
  return (raw || '').trim();
}

/**
 * Odometer / Mileage value for a WorkCompleted group (same source as labour rows).
 */
export function getMechanicMileageForWorkCompletedGroup(form: Form, groupId: string): string {
  const groups = groupMechanicFieldsByWorkIndex(form);
  return pickMechanicMileageFromGroup(groups[groupId] || []);
}

/**
 * Format an Odometer / Mileage value for display: adds thousand separators to the numeric portion
 * (e.g. "100000" -> "100,000", "100000.5 km" -> "100,000.5 km"). Returns the raw input when it
 * doesn't contain a parseable number so we never lose user-entered formatting.
 */
export function formatMechanicMileageDisplay(value: string | undefined | null): string {
  if (value == null) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  // Capture optional sign, integer digits (possibly with existing commas), and optional decimal part.
  const match = trimmed.match(/^([+-]?)(\d[\d,]*)(\.\d+)?(.*)$/);
  if (!match) return trimmed;
  const [, sign, intPartRaw, decPart, rest] = match;
  const intDigits = intPartRaw.replace(/,/g, '');
  if (!intDigits) return trimmed;
  const grouped = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const suffix = (rest || '').trimStart();
  const base = `${sign}${grouped}${decPart || ''}`;
  return suffix ? `${base} ${suffix}` : base;
}

/**
 * Activity type for a WorkCompleted group (used to decide if Odometer / Unit-specific UI applies).
 */
function pickMechanicActivityTypeFromGroup(g: FormField[]): string {
  return (
    g.find(
      (f) =>
        f.field === 'ActivityType' &&
        (f.path.includes('Unit') || f.path.includes('WorkCompleted'))
    )?.value ||
    g.find((f) => f.field === 'ActivityType')?.value ||
    ''
  );
}

/**
 * Whether the given WorkCompleted group is a Vehicle Work and Repairs activity (Odometer is meaningful only for these).
 */
export function isMechanicGroupVehicleWorkAndRepairs(form: Form, groupId: string): boolean {
  const groups = groupMechanicFieldsByWorkIndex(form);
  const activityType = pickMechanicActivityTypeFromGroup(groups[groupId] || []);
  return isVehicleWorkAndRepairsActivityType(activityType);
}

/**
 * Per-activity employee classification from Mechanic Daily Log (TrueContext `Classification` under MechanicTasks…Activity),
 * then timecard / header fallbacks.
 */
function resolveMechanicEmployeeClassification(g: FormField[], form: Form): string {
  const perActivity =
    g.find(
      (f) =>
        f.field === 'Classification' &&
        (f.path.includes('MechanicTasks') || f.path.includes('.Tasks.Activity'))
    )?.value?.trim() || '';

  if (perActivity) return perActivity;

  return (
    g.find((f) => f.field === 'EmployeeClass 2')?.value?.trim() ||
    form.fields.find((f) => f.field === 'EmployeeClass' && f.path.includes('EmployeeMetadata'))?.value?.trim() ||
    form.fields.find((f) => f.field === 'EmployeeClass' && f.path.includes('Daily Log Setup'))?.value?.trim() ||
    g.find((f) => f.field === 'EmployeeClass')?.value?.trim() ||
    'Labour'
  );
}

function fieldInWorkGroup(field: FormField, groupId: string): boolean {
  const baseGroupId = groupId.split('::')[0] || groupId;
  if (!baseGroupId.includes('.')) {
    return (
      field.path.includes(`New Page.WorkCompleted.${baseGroupId}.`) ||
      field.path.includes(`WorkCompleted.${baseGroupId}.WorkCompleted.`) ||
      field.path.includes(`WorkCompleted.Tasks 1.${baseGroupId}.`)
    );
  }
  const minor = baseGroupId.split('.')[1] ?? '';
  return (
    field.path.includes(`WorkCompleted.Tasks ${baseGroupId}.`) ||
    field.path.includes(`New Page.WorkCompleted.${minor}.`) ||
    field.path.includes(`WorkCompleted.${minor}.WorkCompleted.`)
  );
}

/** Task / TaskId fields backing a mechanic Work Completed activity tab. */
export interface MechanicTaskFields {
  taskField?: FormField;
  taskIdField?: FormField;
  /** Task id currently assigned to the group, or null when neither field holds a number. */
  currentTaskId: number | null;
}

const findTaskIdField = (fields: FormField[]): FormField | undefined =>
  fields.find((f) => f.field === 'TaskId' || /\.TaskId$/.test(f.path));

const findTaskField = (fields: FormField[]): FormField | undefined =>
  fields.find((f) => f.field === 'Task' && !f.path.includes('TaskId'));

const parseTaskId = (value: string | undefined): number | null => {
  const parsed = parseInt(String(value ?? '').trim(), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Resolve the Task / TaskId fields for a mechanic Work Completed group so the Detailed view can
 * show and reassign the task (RB parity - see FormCompleteView's task header).
 *
 * A mechanic log stores the task either on the activity instance or on the parent Work Completed
 * block, so look in the per-activity slice first and fall back to the whole block, per field.
 * Weekly payroll reads the mechanic task the same way (`weeklyMechanicFormSource.normalizeMechanicActivityRaw`).
 */
export function getMechanicTaskFieldsForGroup(form: Form, groupId: string): MechanicTaskFields {
  const [activityFields, blockFields] = mechanicGroupFieldSets(form, groupId);

  const taskIdField = findTaskIdField(activityFields) ?? findTaskIdField(blockFields);
  const taskField = findTaskField(activityFields) ?? findTaskField(blockFields);

  return {
    taskField,
    taskIdField,
    currentTaskId: parseTaskId(taskField?.value) ?? parseTaskId(taskIdField?.value)
  };
}

/**
 * The two field sets a mechanic group lookup searches, in order: the per-activity slice, then the
 * whole Work Completed block. An activity may carry only some of a related set of fields.
 */
function mechanicGroupFieldSets(form: Form, groupId: string): [FormField[], FormField[]] {
  return [
    groupMechanicFieldsByWorkIndex(form)[groupId] ?? [],
    form.fields.filter((f) => fieldInWorkGroup(f, groupId))
  ];
}

/** Fields that carry a mechanic group's activity, which is what a Task is on a Road & Bridge log. */
export interface MechanicActivityFields {
  /** TrueContext `ActivityNumber` under …MechanicTasks.N.Tasks.Activity. */
  activityNumberField?: FormField;
  /** TrueContext `Activity` under the same path - some logs carry this instead. */
  activityField?: FormField;
  /** `ActivityName` under ActivityMetadata. */
  activityNameField?: FormField;
  /**
   * `ActivityNumberTimber` - the Timberline-formatted variant that
   * {@link mechanicDailyLogDefectsFlow} posts to Power Automate. Kept in step so a reassignment
   * does not leave the defects payload pointing at the old activity.
   */
  activityTimberField?: FormField;
  currentActivityNumber: string;
  currentActivityName: string;
}

/**
 * `…MechanicTasks.N.Tasks.Activity.…` and the split-activity form `…MechanicTasks.N.Tasks.M.Activity.…`
 * (see {@link groupMechanicFieldsByWorkIndex}, which splits on that same `Tasks.M` index).
 */
const inMechanicActivityPath = (f: FormField): boolean =>
  /MechanicTasks\.\d+\.Tasks(?:\.\d+)?\.Activity[./]/.test(f.path);

/**
 * Resolve the activity fields for a mechanic Work Completed group.
 *
 * A mechanic log has no Task: the effort builders put the activity number in the `task` slot and
 * hard-code `taskId: 0` (see {@link buildEffortsForMechanicGroup}), and the summary table relabels
 * those columns "Activity number / Activity name". Reassigning the activity is therefore the
 * mechanic equivalent of reassigning the task on a Road & Bridge log.
 */
export function getMechanicActivityFieldsForGroup(
  form: Form,
  groupId: string
): MechanicActivityFields {
  const sets = mechanicGroupFieldSets(form, groupId);
  const pick = (predicate: (f: FormField) => boolean): FormField | undefined => {
    for (const fields of sets) {
      const hit = fields.find(predicate);
      if (hit) return hit;
    }
    return undefined;
  };

  const activityNumberField = pick((f) => f.field === 'ActivityNumber' && inMechanicActivityPath(f));
  const activityField = pick((f) => f.field === 'Activity' && inMechanicActivityPath(f));
  const activityNameField = pick(
    (f) =>
      f.field === 'ActivityName' &&
      (f.path.includes('ActivityMetadata') || f.path.includes('MechanicTasks'))
  );
  const activityTimberField = pick(
    (f) => f.field === 'ActivityNumberTimber' && f.path.includes('MechanicTasks')
  );

  return {
    activityNumberField,
    activityField,
    activityNameField,
    activityTimberField,
    currentActivityNumber:
      activityNumberField?.value?.trim() ||
      activityTimberField?.value?.trim() ||
      activityField?.value?.trim() ||
      '',
    currentActivityName: activityNameField?.value?.trim() || ''
  };
}

/**
 * Timberline form of an activity number, for `ActivityNumberTimber`.
 *
 * Mirrors the server's `departmentFromActivityNumber`
 * (`modules/reports/weekly-payroll/weeklyMechanicFormSource.ts`), which is the sync, no-DB rule the
 * mechanic payroll path already applies - values that are already hyphenated or alphanumeric pass
 * through untouched, plain digits become `0NN-NNNN`. Keep the two in step.
 */
export function formatMechanicActivityNumberTimber(raw: string | undefined): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  if (/^\d{3}-/.test(trimmed) || (/[A-Za-z]/.test(trimmed) && trimmed.includes('-'))) {
    return trimmed;
  }
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return trimmed;
  const padded = digits.padStart(6, '0');
  return `0${padded.slice(0, 2)}-${padded.slice(2)}`;
}

function buildEffortsForMechanicGroup(
  groupId: string,
  fields: FormField[],
  form: Form,
  homeForemanAreaId?: number
): MechanicEffortSummaryRow[] {
  const inGroup = (f: FormField) => fieldInWorkGroup(f, groupId);
  const g = fields.filter(inGroup);
  const isSplitActivityGroup = groupId.includes('::');
  const labourHours = resolveMechanicLabourHours(g, {
    preferActivityDuration: isSplitActivityGroup
  });

  const classRateRaw =
    g.find((f) => f.field === 'ClassRate')?.value ?? g.find((f) => f.field === 'Rate')?.value;
  const rate = parseRate(classRateRaw);

  const activityNumber =
    g.find(
      (f) =>
        (f.field === 'ActivityNumber' || f.field === 'Activity') &&
        (f.path.includes('MechanicTasks') || f.path.includes('.Tasks.Activity') || f.path.includes('Activity'))
    )?.value ||
    g.find((f) => f.field === 'ActivityNumber')?.value ||
    '';

  const activityName =
    g.find(
      (f) =>
        f.field === 'ActivityName' &&
        (f.path.includes('ActivityMetadata') || f.path.includes('Activity') || f.path.includes('WorkCompleted'))
    )?.value ||
    g.find((f) => f.field === 'ActivityName')?.value ||
    '';

  const activityType =
    g.find((f) => f.field === 'ActivityType' && (f.path.includes('Unit') || f.path.includes('WorkCompleted')))?.value ||
    g.find((f) => f.field === 'ActivityType')?.value ||
    '';

  const employeeClassification = resolveMechanicEmployeeClassification(g, form);
  const unitDescription = pickMechanicUnitDescriptionFromGroup(g);
  const isVwr = isVehicleWorkAndRepairsActivityType(activityType);
  const labourDescription =
    isVwr && unitDescription ? unitDescription : employeeClassification;

  const activityComments = pickMechanicActivityCommentsFromGroup(g);
  const commentsForRows = activityComments || undefined;
  // Odometer / Mileage only applies to Vehicle Work and Repairs activities (per Unit metadata).
  const mileageForRows = isVwr ? pickMechanicMileageFromGroup(g) || undefined : undefined;

  const efforts: MechanicEffortSummaryRow[] = [];

  if (labourHours > 0) {
    efforts.push({
      type: 'Labour',
      description: labourDescription,
      quantity: labourHours,
      rate,
      totalCost: labourHours * rate,
      unitOfMeasure: 'Hours',
      task: activityNumber || '-',
      taskName: activityName || '-',
      asset: activityType || '',
      assetNumber: '',
      homeForemanAreaId,
      taskId: 0,
      comments: commentsForRows,
      mileage: mileageForRows,
      mechanicGroupId: groupId
    });
  }

  const primaryVehicleNumber =
    g.find((f) => f.field === 'OtherVehicleDescri 1')?.value ||
    g.find((f) => f.field === 'VehicleUnitNumber 1')?.value ||
    g.find(
      (f) =>
        f.field === 'Unit' &&
        f.path.includes('WorkCompleted') &&
        !f.path.includes('DefectVehicle')
    )?.value ||
    g.find((f) => f.field === 'MechanicTimberline')?.value;

  const primaryVehicleRate = g.find(
    (f) => f.field === 'VehicleRate' && !f.path.includes('VehicleUsage')
  )?.value;
  const primaryVehicleHours =
    g.find((f) => f.field === 'HoursVehicleUsage')?.value ||
    g.find((f) => f.field === 'VehicleUsageHours')?.value;

  const vuDurationRaw = g.find(
    (f) => f.field === 'DurationUsage' && f.path.includes('VehicleUsage')
  )?.value;
  const vuHoursParsed =
    parseHoursFromStoredDuration(vuDurationRaw) || safeParseFloat(vuDurationRaw);
  const vuUsed = g.find(
    (f) => f.field === 'VehicleUsed' && f.path.includes('VehicleUsage')
  )?.value?.trim();
  const vuRateVal = g.find(
    (f) => f.field === 'VehicleRate' && f.path.includes('VehicleUsage')
  )?.value;

  if (vuHoursParsed > 0) {
    const vr = parseRate(vuRateVal) || safeParseFloat(vuRateVal, 1);
    efforts.push({
      type: 'Vehicle',
      description: vuUsed || 'Vehicle usage',
      quantity: vuHoursParsed,
      rate: vr,
      totalCost: vuHoursParsed * vr,
      unitOfMeasure: 'Hours',
      task: activityNumber || '-',
      taskName: activityName || '-',
      asset: activityType || '',
      assetNumber: '',
      homeForemanAreaId,
      taskId: 0,
      comments: commentsForRows,
      mileage: mileageForRows
    });
  } else if (primaryVehicleNumber && primaryVehicleHours && safeParseFloat(primaryVehicleHours) > 0) {
    const vh = safeParseFloat(primaryVehicleHours);
    const vr = parseRate(primaryVehicleRate) || safeParseFloat(primaryVehicleRate, 1);
    efforts.push({
      type: 'Vehicle',
      description: `Unit #${primaryVehicleNumber}`,
      quantity: vh,
      rate: vr,
      totalCost: vh * vr,
      unitOfMeasure: 'Hours',
      task: activityNumber || '-',
      taskName: activityName || '-',
      asset: activityType || '',
      assetNumber: '',
      homeForemanAreaId,
      taskId: 0,
      comments: commentsForRows,
      mileage: mileageForRows
    });
  }

  const secondaryVehicleNumber =
    g.find((f) => f.field === 'OtherVehicleDescri 2')?.value ||
    g.find((f) => f.field === 'SecondaryUnitNumber')?.value;
  const secondaryVehicleRate = g.find((f) => f.field === 'SecondaryVehicleRate')?.value;
  const secondaryVehicleHours = g.find((f) => f.field === 'Hours Secondary Vehi')?.value;

  if (secondaryVehicleNumber && secondaryVehicleHours && safeParseFloat(secondaryVehicleHours) > 0) {
    const vh = safeParseFloat(secondaryVehicleHours);
    const vr = parseRate(secondaryVehicleRate) || safeParseFloat(secondaryVehicleRate, 1);
    efforts.push({
      type: 'Vehicle',
      description: `Unit #${secondaryVehicleNumber}`,
      quantity: vh,
      rate: vr,
      totalCost: vh * vr,
      unitOfMeasure: 'Hours',
      task: activityNumber || '-',
      taskName: activityName || '-',
      asset: activityType || '',
      assetNumber: '',
      homeForemanAreaId,
      taskId: 0,
      comments: commentsForRows,
      mileage: mileageForRows
    });
  }

  const mealDurationMinutes = g.find((f) => f.field === 'MealDurationMinutes')?.value;

  if (mealDurationMinutes && safeParseFloat(mealDurationMinutes) > 0) {
    const mealHours = safeParseFloat(mealDurationMinutes) / 60;
    efforts.push({
      type: 'Meal',
      description: 'Meal Break',
      quantity: mealHours,
      rate: 0,
      totalCost: 0,
      unitOfMeasure: 'Hours',
      task: activityNumber || '-',
      taskName: activityName || '-',
      asset: activityType || '',
      assetNumber: '',
      homeForemanAreaId,
      taskId: 0,
      comments: commentsForRows,
      mileage: mileageForRows
    });
  }

  return efforts;
}

function parseCurrencyField(value: string | undefined): number {
  if (!value) return 0;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return isNaN(n) ? 0 : n;
}

/** RB-style effort rows for Daily Log - Mechanic (summary + title totals). */
export function buildMechanicEffortSummaries(form: Form): MechanicEffortSummaryRow[] {
  const groups = groupMechanicFieldsByWorkIndex(form);
  const homeForemanAreaId = form.homeForemanAreaId;
  const keys = Object.keys(groups).sort(sortGroupKeys);
  const all: MechanicEffortSummaryRow[] = [];
  for (const groupId of keys) {
    all.push(
      ...buildEffortsForMechanicGroup(groupId, groups[groupId], form, homeForemanAreaId)
    );
  }

  if (all.length === 0) {
    const dec = parseCurrencyField(
      form.fields.find((f) => f.field === 'TotalLabourTimeDec')?.value
    );
    if (dec > 0) {
      const rate = parseCurrencyField(
        form.fields.find((f) => f.field === 'ClassRate')?.value ??
          form.fields.find((f) => f.field === 'Rate')?.value
      );
      const workCompletedFields = form.fields.filter((f) => f.path.includes('WorkCompleted'));
      const employeeClassification =
        workCompletedFields.find(
          (f) =>
            f.field === 'Classification' &&
            (f.path.includes('MechanicTasks') || f.path.includes('.Tasks.Activity'))
        )?.value?.trim() || resolveMechanicEmployeeClassification([], form);
      const fallbackActivityType =
        workCompletedFields.find((f) => f.field === 'ActivityType')?.value ||
        form.fields.find((f) => f.field === 'ActivityType')?.value;
      const fallbackUnit = pickMechanicUnitDescriptionFromGroup(workCompletedFields);
      const fallbackIsVwr = isVehicleWorkAndRepairsActivityType(fallbackActivityType);
      const labourDescriptionFallback =
        fallbackIsVwr && fallbackUnit ? fallbackUnit : employeeClassification;
      const fallbackComments =
        pickMechanicActivityCommentsFromGroup(workCompletedFields) || undefined;
      const fallbackMileage = fallbackIsVwr
        ? pickMechanicMileageFromGroup(workCompletedFields) || undefined
        : undefined;
      all.push({
        type: 'Labour',
        description: labourDescriptionFallback,
        quantity: dec,
        rate,
        totalCost: dec * rate,
        unitOfMeasure: 'Hours',
        task: '-',
        taskName: '-',
        asset: '',
        assetNumber: '',
        homeForemanAreaId,
        taskId: 0,
        comments: fallbackComments,
        mileage: fallbackMileage,
        mechanicGroupId: 'form'
      });
    }
  }

  return all;
}

const upsertDefect = (
  defectsByIndex: Record<string, MechanicDefect>,
  defectIndex: string,
  field: FormField
): void => {
  if (!defectsByIndex[defectIndex]) {
    defectsByIndex[defectIndex] = {
      defectId: '',
      category: '',
      comment: '',
      completed: ''
    };
  }

  if (field.field === 'DefectID') defectsByIndex[defectIndex].defectId = field.value || '';
  if (field.field === 'DefectCategory 1') defectsByIndex[defectIndex].category = field.value || '';
  if (field.field === 'DefectComment 1') defectsByIndex[defectIndex].comment = field.value || '';
  if (field.field === 'DefectCompleted') defectsByIndex[defectIndex].completed = field.value || '';
};

export const getMechanicWorkCompletedRows = (form: Form): MechanicWorkCompletedRow[] => {
  const fieldsByGroup = groupMechanicFieldsByWorkIndex(form);

  return Object.entries(fieldsByGroup)
    .sort(([a], [b]) => sortGroupKeys(a, b))
    .map(([groupId, gFields]) => {
      const defectsByIndex: Record<string, MechanicDefect> = {};
      gFields.forEach((field) => {
        if (
          field.field === 'DefectID' ||
          field.field === 'DefectCategory 1' ||
          field.field === 'DefectComment 1' ||
          field.field === 'DefectCompleted'
        ) {
          const indexedMatch = field.path.match(/Defects\.(\d+)\./);
          const defectIndex = indexedMatch?.[1] || '0';
          upsertDefect(defectsByIndex, defectIndex, field);
        }
      });

      const activityName =
        gFields.find((field) => field.field === 'ActivityName' && field.value)?.value ||
        gFields.find((field) => field.field === 'Activity' && field.value)?.value ||
        '';
      const activityType =
        gFields.find((field) => field.field === 'ActivityType' && field.value)?.value || '';
      const hoursWorked =
        gFields.find((field) => field.field === 'TaskDurationHours' && field.value)?.value ||
        gFields.find((field) => field.field === 'TotalTime' && field.value)?.value ||
        '';

      return {
        groupId,
        unitLabel: pickMechanicUnitDescriptionFromGroup(gFields),
        activityType,
        activity: activityName,
        hoursWorked,
        defects: Object.values(defectsByIndex).filter(
          (defect) => defect.defectId || defect.category || defect.comment || defect.completed
        )
      };
    });
};
